import { useMemo, useRef } from 'react';
import { convertiUtsToDate } from '../../GrapheXT/GrapheXT';
import {
  SIGNAL_DOMAIN,
  LABEL_DOMAIN,
  SECTION_ALPHA,
} from '../utils/labelConstants';
import { buildAnnotation, buildEventTraces } from '../utils/plotlyBuilders';

const usePlotlyData = (
  donnees,
  configuration,
  localLabels,
  localPhases,
  graphCurves
) => {
  const sectionsRef = useRef([]);
  const tRawRef = useRef([]);
  const labelsRawRef = useRef([]);

  const { allTraces, annotations, layout } = useMemo(() => {
    // ── Traces signal ─────────────────────────────────────────────────────────
    const signalTraces = donnees.map((curve, curveIndex) => {
      const tRaw = curve.t || curve.x || [];
      if (curveIndex === 0) tRawRef.current = tRaw;
      const gc = graphCurves?.[curveIndex] || {};
      return {
        ...curve,
        type: gc.type || curve.type || 'scatter',
        mode: gc.mode || curve.mode || 'lines',
        x: convertiUtsToDate(tRaw),
        y: curve.y || [],
        yaxis: 'y',
        name:
          gc.name ||
          curve.name ||
          curve.sensor_name ||
          `Signal ${curveIndex + 1}`,
        hovertemplate: '%{x}<br>%{y}<extra></extra>',
        line: {
          ...(curve.line || {}),
          color: gc.color ?? curve.line?.color ?? curve.color,
          dash: gc.line_dash || curve.line?.dash,
          shape: gc.line_shape || curve.line?.shape,
        },
        meta: { formula: gc.formula || null },
        marker: { color: gc.color ?? curve.color },
      };
    });

    const allYValues = donnees.flatMap((d) => d.y || []);
    const yMax = allYValues.length ? Math.max(...allYValues) : 1;
    const yMin = allYValues.length ? Math.min(...allYValues) : 0;
    const yRange = yMax - yMin || 1;

    const tBase = donnees[0]?.x || donnees[0]?.t || [];
    const tTotal = tBase.length > 1 ? tBase[tBase.length - 1] - tBase[0] : 1;
    const markerSize = configuration.markerSize || 16;
    const shownLabels = new Set();

    // ── Labels ────────────────────────────────────────────────────────────────
    const labelsRaw = [
      ...(Array.isArray(configuration.labels)
        ? configuration.labels
        : Array.isArray(configuration.events)
          ? configuration.events
          : []),
      ...localLabels,
    ].filter((l) => l.t != null && Number.isFinite(Number(l.t)));
    labelsRawRef.current = labelsRaw;

    const { traces: labelTraces, annMeta: labelAnnMeta } = buildEventTraces({
      items: labelsRaw,
      tRaw: tBase,
      yRaw: donnees[0]?.y || [],
      donnees,
      yMax,
      tTotal,
      domain: LABEL_DOMAIN,
      yaxisKey: 'y2',
      markerSize,
      shownLabels,
      graphCurves,
    });

    // ── Sections ──────────────────────────────────────────────────────────────
    const sectionsRaw = [
      ...(Array.isArray(configuration.sections) ? configuration.sections : []),
      ...localPhases,
    ].filter(
      (s) =>
        s.tStart != null &&
        Number.isFinite(Number(s.tStart)) &&
        s.tEnd != null &&
        Number.isFinite(Number(s.tEnd))
    );
    sectionsRef.current = sectionsRaw;

    const sectionShapes = sectionsRaw.map((sec) => {
      const x0 = convertiUtsToDate([sec.tStart])[0];
      const x1 = convertiUtsToDate([sec.tEnd])[0];
      const color = sec.color || '#888888';
      return {
        type: 'rect',
        xref: 'x',
        yref: 'paper',
        x0,
        x1,
        y0: SIGNAL_DOMAIN[0],
        y1: SIGNAL_DOMAIN[1],
        fillcolor: color + SECTION_ALPHA,
        line: { width: 0 },
        layer: 'below',
      };
    });

    const sectionAnnotations = sectionsRaw.map((sec) => {
      const xStart = convertiUtsToDate([sec.tStart])[0];
      const color = sec.color || '#888888';
      const title = sec.label
        ? `<b>${sec.label}</b>`
        : '<i style="color:#aaa">Phase</i>';
      return {
        x: xStart,
        xref: 'x',
        xanchor: 'left',
        y: SIGNAL_DOMAIN[1],
        yref: 'paper',
        yanchor: 'top',
        text: `${title} <span style="font-size:10px">✏</span>`,
        showarrow: false,
        font: { size: 11, color, family: 'Montserrat, sans-serif' },
        bgcolor: 'rgba(255,255,255,0.88)',
        bordercolor: color,
        borderwidth: 1,
        borderpad: 4,
        captureevents: true,
      };
    });

    const bandShapes = [
      {
        type: 'rect',
        xref: 'paper',
        yref: 'paper',
        x0: 0,
        x1: 1,
        y0: LABEL_DOMAIN[0] - 0.01,
        y1: LABEL_DOMAIN[1],
        fillcolor: 'rgba(240,243,255,0.6)',
        line: { width: 1, color: '#d0d5ee' },
        layer: 'below',
      },
    ];

    const plotYMax = yMax + yRange * 0.08;

    // Lignes de connexion pointillées — deux traces par label (signal + bande).
    const dotLineTraces = labelAnnMeta.flatMap((ev) => [
      {
        type: 'scatter',
        mode: 'lines',
        legendgroup: ev.legendGroup,
        showlegend: false,
        hoverinfo: 'skip',
        name: '',
        xaxis: 'x',
        yaxis: 'y',
        x: [ev.xDate, ev.xDate],
        y: [ev.yCurveVal, plotYMax * 100],
        line: { color: ev.style.color, width: 1.5, dash: 'dot' },
      },
      {
        type: 'scatter',
        mode: 'lines',
        legendgroup: ev.legendGroup,
        showlegend: false,
        hoverinfo: 'skip',
        name: '',
        xaxis: 'x',
        yaxis: 'y2',
        x: [ev.xDate, ev.xDate],
        y: [0, ev.markerY],
        line: { color: ev.style.color, width: 1.5, dash: 'dot' },
      },
    ]);

    // ── Hover phantoms ────────────────────────────────────────────────────────
    const N_HOVER = 20;
    const yHover = yMax + yRange * 0.04;
    const N_LABEL_HOVER = 10;

    const phaseHoverTraces = sectionsRaw.map((sec) => {
      const phaseData = {
        type: 'phase',
        tStart: sec.tStart,
        tEnd: sec.tEnd,
        label: sec.label,
        color: sec.color,
      };
      const xPoints = Array.from({ length: N_HOVER }, (_, i) => {
        const t = sec.tStart + ((sec.tEnd - sec.tStart) * i) / (N_HOVER - 1);
        return convertiUtsToDate([Math.round(t)])[0];
      });
      return {
        type: 'scatter',
        mode: 'markers',
        hoverinfo: 'none',
        showlegend: false,
        name: '',
        xaxis: 'x',
        yaxis: 'y',
        x: xPoints,
        y: Array(N_HOVER).fill(yHover),
        marker: {
          size: 18,
          color: 'rgba(0,0,0,0)',
          symbol: 'square',
          line: { width: 0 },
        },
        customdata: Array(N_HOVER).fill(phaseData),
      };
    });

    const labelHoverTraces = labelsRaw.map((ev) => {
      const xDate = convertiUtsToDate([ev.t])[0];
      return {
        type: 'scatter',
        mode: 'markers',
        hoverinfo: 'none',
        showlegend: false,
        name: '',
        xaxis: 'x',
        yaxis: 'y2',
        x: Array(N_LABEL_HOVER).fill(xDate),
        y: Array.from(
          { length: N_LABEL_HOVER },
          (_, i) => i / (N_LABEL_HOVER - 1)
        ),
        marker: {
          size: 10,
          color: 'rgba(0,0,0,0)',
          symbol: 'square',
          line: { width: 0 },
        },
        customdata: Array(N_LABEL_HOVER).fill(ev.t),
      };
    });

    // ── Layout ────────────────────────────────────────────────────────────────
    const layout = {
      title: {
        text: `${configuration.title || ''}${configuration.subtitle ? '<br>' + configuration.subtitle : ''}`,
      },
      paper_bgcolor: 'white',
      plot_bgcolor: 'white',
      xaxis: {
        title: { text: configuration.xaxis?.title || 't' },
        type: 'date',
        domain: [0, 1],
        showgrid: true,
        zeroline: false,
        anchor: 'y',
      },
      yaxis: {
        title: { text: configuration.yaxis?.title || '' },
        domain: SIGNAL_DOMAIN,
        range: [yMin - yRange * 0.05, yMax + yRange * 0.08],
        showgrid: true,
        zeroline: false,
      },
      yaxis2: {
        domain: LABEL_DOMAIN,
        range: [0, 1],
        fixedrange: true,
        visible: false,
        anchor: 'x',
      },
      shapes: [...sectionShapes, ...bandShapes],
      annotations: [...sectionAnnotations],
      legend: {
        orientation: 'v',
        x: 1.02,
        y: 1,
        xanchor: 'left',
        yanchor: 'top',
        bgcolor: 'rgba(255,255,255,0.9)',
        bordercolor: '#ddd',
        borderwidth: 1,
        tracegroupgap: 8,
        groupclick: 'togglegroup',
        font: { size: 12 },
      },
      margin: { l: 80, r: 230, t: 60, b: 60 },
    };

    const annotations = labelAnnMeta.map((ev) => {
      const paperY =
        LABEL_DOMAIN[0] + (LABEL_DOMAIN[1] - LABEL_DOMAIN[0]) * ev.markerY;
      return buildAnnotation({
        xDate: ev.xDate,
        paperY,
        label: ev.label,
        dateStr: ev.dateStr,
        note: ev.note,
        color: ev.style.color,
        valLine: ev.valLine,
      });
    });

    return {
      allTraces: [
        ...signalTraces,
        ...labelTraces,
        ...phaseHoverTraces,
        ...labelHoverTraces,
        ...dotLineTraces,
      ],
      annotations,
      layout,
    };
  }, [donnees, configuration, localLabels, localPhases, graphCurves]);

  return { allTraces, annotations, layout, sectionsRef, tRawRef, labelsRawRef };
};

export default usePlotlyData;
